import moment from 'moment'

import { getI18n } from '@/plugins/i18n'
import { minutesToHHmm } from '@/lib/utils'

import { useDateTimeColumn } from '@/composables/data-grid-column'

import { useWebSiteStore } from '@/stores/web-site.store'

export const useColumns = (editors) => {
  const i18n = getI18n()
  const webSiteStore = useWebSiteStore()

  const calculateDuration = (start, end) => {
    if (!start || !end) return null
    const minutes = Math.ceil((new Date(end) - new Date(start)) / 60000)
    return Math.max(0, minutes)
  }

  const calculateEnd = (start, duration) => {
    if (!start || duration == null) return null
    return new Date(new Date(start).getTime() + duration * 60000)
  }

  return [
    {
      type: 'buttons',
      buttons: [{template: 'editButtonsTemplate'}],
      width: '5.75rem',
      visibleIndex: 0,
      fixed: false
    },
    useDateTimeColumn({
      sortOrder: 'desc',
      dataField: 'start',
      caption: i18n.t('common.start'),
      setCellValue: (newData, value, currentRowData) => {
        newData.start = value ? moment(value).toISOString() : null
        newData.is_manual = true
        const end = newData.end ?? moment.utc(currentRowData.end?.toString()).local().toDate()
        newData.duration_minutes = calculateDuration(newData.start, end)

        const startDate = new Date(newData.start)
        editors['end'].option('min', startDate)
        editors['end'].option('disabledDates', (d) => {
          return !moment(d.date).isSame(startDate, 'day')
        })
      },
      validationRules: [{ type: 'required' }]
    }),
    useDateTimeColumn({
      dataField: 'end',
      caption: i18n.t('common.end'),
      setCellValue: (newData, value, currentRowData) => {
        newData.end = value ? moment(value).toISOString() : null
        newData.start = moment.utc(currentRowData.start?.toString()).local().toISOString()
        newData.is_manual = true

        const start = newData.start ?? moment.utc(currentRowData.start?.toString()).local().toDate()

        newData.duration_minutes = calculateDuration(start, newData.end)
      },
      validationRules: [
        {
          type: 'custom',
          validationCallback: (e) => {
            const end = e.value
            const start = moment.utc(e.data?.start.toString()).local().toDate()

            if (!end || !start) return true

            return new Date(end) > new Date(start)
          },
          message: i18n.t('error.validation.endAfterStart')
        },
        {
          type: 'custom',
          validationCallback: (e) => {
            if (!e.value) return true

            return new Date(e.value) < new Date(Date.now())
          },
          message: i18n.t('error.validation.endInFuture')
        },
        {
          type: 'custom',
          validationCallback: (e) => {
            let end
            if (e.value !== '') {
              end = e.value
            } else if (e.data?.end) {
              end = moment.utc(e.data.end.toString()).local()
            } else {
              end = new Date()
            }
            const start = moment.utc(e.data?.start?.toString()).local()

            if (!start) return true

            const endMoment = moment(end)

            return endMoment.isSame(start, 'day')
          },
          message: i18n.t('error.validation.endSameDay')
        }
      ],
    }),
    {
      caption: i18n.t('common.mechanic'),
      dataField: 'mechanic',
      lookup: {
        dataSource: webSiteStore.userDetails.filter(i => i.is_mechanic),
        valueExpr: 'id',
        displayExpr: (item) => `${item.name} ${item.name_ext}`,
      },
      setCellValue: (newData, value, currentRowData) => {
        newData.mechanic = value
        newData.start = moment.utc(currentRowData.start?.toString()).local().toISOString()
      },
      validationRules: [{ type: 'required' }]
    },
    {
      caption: `${i18n.t('common.workedTime')}`,
      dataField: 'duration_minutes',
      dataType: 'number',
      alignment: 'center',
      editorOptions: { min: 0 },
      calculateSortValue: 'duration_minutes',
      calculateDisplayValue: (rowData) => {
        const minutes = rowData?.duration_minutes
        return minutesToHHmm(minutes)
      },
      setCellValue: (newData, value, currentRowData) => {
        newData.duration_minutes = value
        newData.is_manual = true
        newData.start = moment.utc(currentRowData.start?.toString()).local().toISOString()

        const start = newData.start ?? moment.utc(currentRowData.start?.toString()).local().toDate()

        const end = calculateEnd(start, value)
        newData.end = end ? moment(end).toISOString() : null
      },
      validationRules: [
        {
          type: 'custom',
          validationCallback: (e) => {
            return e.value == null || e.value > 0
          },
          message: i18n.t('error.validation.endAfterStart')
        }
      ],
    },
    {
      caption: `${i18n.t('common.billableTime')}`,
      dataField: 'billed_minutes',
      dataType: 'number',
      alignment: 'center',
      calculateSortValue: 'billed_minutes',
      calculateDisplayValue: (rowData) => {
        const minutes = rowData?.billed_minutes
        return minutesToHHmm(minutes)
      },
    },
    { caption: i18n.t('common.isManuallyInserted'), dataField: 'is_manual', dataType: 'boolean' },
    { caption: i18n.t('common.isConfirmed'), dataField: 'is_confirmed', dataType: 'boolean', visible: false, showInColumnChooser: false },
    {
      caption: i18n.t('common.note'),
      dataField: 'notes',
      dataType: 'string',
      setCellValue: (newData, value, currentRowData) => {
        newData.notes = value
        newData.start = moment.utc(currentRowData.start?.toString()).local().toISOString()
      },
    },
    { dataField: 'activity_code', visible: false, showInColumnChooser: false }
  ]
}
